<template>
  <div v-bind="$attrs" class="canvas-selector-container h-full" />
  <NModal :auto-focus="false" close-on-esc :on-esc="cancel" show to=".canvas-selector-container">
    <NCard
      :bordered="false"
      class="section-selector__card rounded-2 h-full"
      :content-style="{ overflowY: 'auto', backgroundColor: themeVars.primary6, borderRadius: '8px' }"
    >
      <CanvasSelectionArea />
      <template #footer>
        <div class="flex gap-2 justify-end mt-2 relative top-1">
          <CButton :disabled="isRestoringSelection" @click="emit('cancel')">
            {{ t('canvasUI.common.cancel') }}
          </CButton>
          <CButton :loading="isRestoringSelection" type="primary" @click="emit('primary-action', selectedItems)">
            {{ t('canvasUI.common.done') }}
          </CButton>
        </div>
      </template>
    </NCard>
  </NModal>
</template>

<script setup lang="ts">
import { NModal, NCard } from 'naive-ui'
import { provide, toRefs, onMounted, onBeforeUnmount, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import CButton from '@lib/components/CButton/CButton.vue'
import CanvasSelectionArea from './components/CanvasSelectionArea.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import useAppStore from '@canvas-selector/stores/app'
import type { Canvas } from '@openapi'
import type { AppProps } from '@canvas-selector/appTypes'
import { availableSelectorTypes } from '@canvas-selector/constants'
import { urlSafeFetchInChunks } from '@lib/util/network'

const { t } = useI18n()
const emit = defineEmits<{
  (e: 'cancel'): void
  (e: 'primary-action', selection: Canvas[]): void
}>()
const props = withDefaults(defineProps<AppProps>(), {
  sequencingKey: 'sequence',
})
const themeVars = useThemeVars()
const isRestoringSelection = ref(false)
provide('appProps', toRefs(props))
const { activeSelectionType, selectedItems, cleanup } = useAppStore()

async function maybeRestoreSelection() {
  try {
    const ids = props.selections?.map((s) => s.id)
    if (!ids?.length) return
    const fetchedCanvases = await urlSafeFetchInChunks<Canvas>(
      ids,
      (chunk) =>
        props.listCanvases?.({ filters: { id__in: chunk.join(',') }, page_size: chunk.length }) as Promise<{
          results: Canvas[]
        }>,
    )

    // Sort canvases according to the original IDs order
    const canvasMap = new Map(fetchedCanvases.map((canvas) => [canvas.id, canvas]))
    selectedItems.value = ids.map((id) => canvasMap.get(id)).filter(Boolean) as Canvas[]
  } catch (e) {
    console.warn('Failed to restore selection', e)
  }
}

onMounted(() => {
  activeSelectionType.value = props.allowedSelectorTypes?.length
    ? props.allowedSelectorTypes?.[0]
    : availableSelectorTypes?.[0]
  maybeRestoreSelection()
})

onBeforeUnmount(() => {
  cleanup()
})

const cancel = () => {
  emit('cancel')
}
</script>

<style lang="scss" scoped>
.canvas-selector-container {
  font-family: Roboto, sans-serif; // stop any themes from altering our canvas selector

  :deep(.n-modal-scroll-content) {
    height: 100%;
    padding: 24px;
  }
}
</style>
